import { ImsObject } from './ims_object';
import { SystemTypeResource } from 'app/shared/resources';

const IMS_TYPE__SYSTEM_TYPE = 'system_type';

export const SUPPORTED_SYSTEM_DATA_TYPES = ['asset', 'ip_device'];

export enum SystemSourceTypes {
    // This means that the source of data for a integration is the child IP Device of an Asset, or the IP Device itself
    // Example: System that polls 3D-P Devices for network data (IP Device) and locations (Asset) from the IP Device data source
    ChildIPDeviceOrSelf = 'child_ip_device_or_self',
    // This means that the source of data for an Asset integration is the child IP Device of an Asset
    // Example: System that polls a GPS IP Device for locations (Asset) from the IP Device data source
    ChildIPDevice = 'child_ip_device',
    // This means that the source of data for an integration is the Asset or IP Device that it's enabled for
    // Example: System that polls data for network stats on an IP Device from the same IP Device data source
    Self = 'self',
    // This means that the source of data for an asset integration is NOT the child IP Device
    // Example: System that polls location data for an Asset from an unrelated IP Device data source such as a database
    NonChildIpDevice = 'non_child_ip_device',
}

export interface DataTypeSpec {
    source_type?: SystemSourceTypes;
}

export interface DataTypesSpec {
    asset?: DataTypeSpec;
    ip_device?: DataTypeSpec;
}

/**
 * @ngdoc service
 * @name ims.shared.data.service:SystemType
 * @description
 * The ims SystemType object.
 * Object structure:
 * {id: 1,
 *  resource:{}}
 *
 * @param {ngResource} systemTypeResource A system type ngResource object.
 */
export class SystemType extends ImsObject {
    constructor(resource: typeof SystemTypeResource) {
        super(IMS_TYPE__SYSTEM_TYPE, resource);
    }

    //show a more descriptive name when used in dropdowns
    get labelName() {
        return this.display_name;
    }

    get name() {
        return this.resource.name;
    }

    get data_types(): DataTypesSpec {
        return this.resource.data_types || {};
    }

    get description(): string {
        return this.resource.description;
    }

    set description(value: string) {
        this.resource.description = value;
    }

    get display_name() {
        return this.resource.display_name;
    }

    get selectable(): boolean {
        // this exists on other objects, but generally at the database/API Model level.
        // this is a special case as it's a "computed" property generally pushed in from the backend.
        return !this.name.includes('DEPRECATED');
    }

    get schema() {
        return this.resource.schema;
    }

    getDataTypeSpec(typeName: string) {
        if (!SUPPORTED_SYSTEM_DATA_TYPES.includes(typeName)) {
            throw new Error(`Unsupported data type ${typeName}`);
        }
        return this.data_types[typeName];
    }

    static toFilterable(systemType: SystemType): object {
        return systemTypeToFilterableSystemType(systemType);
    }
}

/**
 * Filterable dictionary
 */
interface FilterableSystemType {
    id: number;
    name: string;
    description: string;
}

function systemTypeToFilterableSystemType(systemType: SystemType): FilterableSystemType {
    return {
        id: systemType.id,
        name: systemType.display_name,
        description: systemType.description,
    };
}
